//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserMultiple, Edit, TrashCan } from '@carbon/icons-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { UserGroupDto } from './types';
import { Skeleton } from '../ui/skeleton';

interface GroupsTableProps {
  groups: UserGroupDto[];
  loading?: boolean;
  currentUserId: string;
  onEdit: (groupId: string) => void;
  onDelete: (groupId: string) => void;
  onToggleOverride: (groupId: string, newValue: boolean) => Promise<void>;
}

const GroupsTable: React.FC<GroupsTableProps> = ({
  groups,
  loading = false,
  currentUserId,
  onEdit,
  onDelete,
  onToggleOverride,
}) => {
  const { t } = useTranslation();
  const [togglingGroupId, setTogglingGroupId] = useState<string | null>(null);

  if (loading) {
    return (
      <div data-testid="groups-table-loading" className="space-y-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div
        data-testid="groups-table-empty"
        className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400"
      >
        <UserMultiple
          size={48}
          className="mb-4 text-gray-400 dark:text-gray-500"
        />
        <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
          {t('userGroups.noGroups')}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="groups-table">
      <Table>
        <TableHeader className="bg-gray-100 dark:bg-[#393939]">
          <TableRow className="border-b border-gray-300 dark:border-gray-600">
            <TableHead className="font-semibold text-gray-700 dark:text-gray-300">
              {t('userGroups.groupName')}
            </TableHead>
            <TableHead className="font-semibold text-gray-700 dark:text-gray-300">
              {t('userGroups.members')}
            </TableHead>
            <TableHead className="font-semibold text-gray-700 dark:text-gray-300">
              {t('userGroups.integrations')}
            </TableHead>
            <TableHead className="font-semibold text-gray-700 dark:text-gray-300">
              {t('userGroups.allowOverride')}
            </TableHead>
            <TableHead className="text-right font-semibold text-gray-700 dark:text-gray-300">
              {t('userGroups.actions')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map((group) => (
            <TableRow
              key={group.id}
              data-testid={`groups-table-row-${group.id}`}
            >
              <TableCell className="font-medium">
                <div className="max-w-xs">
                  <div className="truncate font-semibold" title={group.name}>
                    {group.name}
                  </div>
                  {group.description && (
                    <div
                      className="truncate text-sm text-gray-500 dark:text-gray-400"
                      title={group.description}
                    >
                      {group.description}
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <UserMultiple
                    size={16}
                    className="text-gray-500 dark:text-gray-400"
                  />
                  <span>{group.memberCount}</span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 dark:text-gray-400">🔌</span>
                  {group.integrationName ? (
                    <span className="font-medium">{group.integrationName}</span>
                  ) : (
                    <span className="italic text-gray-400 dark:text-gray-500">
                      {t('userGroups.none')}
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell>
                {group.createdBy === currentUserId ? (
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={group.allowUserOverride}
                      onCheckedChange={async (checked) => {
                        setTogglingGroupId(group.id);
                        try {
                          await onToggleOverride(group.id, checked);
                        } finally {
                          setTogglingGroupId(null);
                        }
                      }}
                      disabled={togglingGroupId === group.id}
                      data-testid={`groups-table-override-toggle-${group.id}`}
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {group.allowUserOverride
                        ? t('userGroups.enabled')
                        : t('userGroups.disabled')}
                    </span>
                  </div>
                ) : (
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {group.allowUserOverride
                      ? t('userGroups.enabled')
                      : t('userGroups.disabled')}
                  </span>
                )}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(group.id)}
                    data-testid={`groups-table-edit-${group.id}`}
                    aria-label={`Edit ${group.name}`}
                  >
                    <Edit size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(group.id)}
                    data-testid={`groups-table-delete-${group.id}`}
                    aria-label={`Delete ${group.name}`}
                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                  >
                    <TrashCan size={16} />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default GroupsTable;
